import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaClock, FaWrench, FaArrowLeft, FaPhoneAlt, FaClipboardList } from 'react-icons/fa';
import { servicesAPI } from '../services/api';
import { useBooking } from '../contexts/BookingContext';
import { useSiteInfo } from '../contexts/SiteInfoContext';
import { normalizePhoneNumbers } from '../utils/businessInfo';
import { getServiceImage } from '../utils/serviceImages';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openBookingModal } = useBooking();
  const { siteInfo } = useSiteInfo();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizedSlug = slug
    ?.replace(/^standard-/, '')
    ?.replace(/^comprehensive-/, '')
    ?.replace(/-package$/, '');
  const apiSlug = normalizedSlug || slug;

  const businessInfo = siteInfo?.business_info ?? {};
  const company = siteInfo?.company ?? {};
  const phoneNumbers = normalizePhoneNumbers(businessInfo, company);
  const companyPhone = phoneNumbers[0]?.number || '0383682249';
  const formattedPhone = companyPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2 $3');

  useEffect(() => {
    let isMounted = true;

    const fetchService = async () => {
      setLoading(true);
      try {
        const response = await servicesAPI.getService(apiSlug);
        if (isMounted) {
          setService(response.data);
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          try {
            const categoryFallback = await servicesAPI.getCategory(apiSlug);
            if (isMounted) {
              setService(categoryFallback.data);
            }
          } catch (catErr) {
            console.error('Category fallback failed:', catErr);
            if (isMounted) {
              setService(null);
            }
          }
        } else {
          console.error('Error fetching service:', err);
          if (isMounted) {
            setService(null);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchService();

    return () => {
      isMounted = false;
    };
  }, [slug, apiSlug]);

  // Fallback data for common services
  const fallbackServices = {
    'minor-service': {
      name: 'Minor Service',
      tagline: 'Essential maintenance to keep your vehicle running smoothly',
      short_description: 'Essential maintenance to keep your vehicle running smoothly',
      description: '<p>Our comprehensive Minor Service ensures your vehicle receives essential maintenance to keep it running safely and efficiently. This service is perfect for regular maintenance between major services.</p>',
      sections: [
        {
          id: 1,
          title: 'Standard Vehicle Inspection',
          content: '<ul><li><strong>Brakes and suspension</strong> - pads & discs, brake lines & hoses, control arms bushes, shocks</li><li><strong>Drive components</strong> - belts, engine mounts, drive shafts and CV boots</li><li><strong>Exhaust system</strong> - condition check and leak detection</li><li><strong>Air filter</strong> - inspection and cleaning</li><li><strong>Lights and wipers</strong> - full functionality check</li><li><strong>Battery health</strong> - test and inspection</li><li><strong>Tyres</strong> - tread depth, wear pattern, and pressures</li></ul>'
        },
        {
          id: 2,
          title: 'Customer Concerns & Diagnostics',
          content: '<p>We scan and diagnose any specific issues reported by the customer and provide a comprehensive report with recommendations and repair plan before any additional work proceeds.</p>'
        }
      ],
      additional_info: '<p>For details and quote please contact us.</p>',
      display_price: 'Contact for quote',
      display_duration: '1-2 hours',
      inclusions: [
        { id: 1, title: 'Replace engine oil and oil filter', description: 'High-quality engine oil and genuine oil filter replacement' },
        { id: 2, title: 'Top up all under-bonnet fluids', description: 'Coolant, brake fluid, power steering, washer fluid' },
        { id: 3, title: 'Brakes and suspension inspection', description: 'Check pads & discs, brake lines & hoses, control arms bushes, shocks' },
        { id: 4, title: 'Drive belts and engine mounts check', description: 'Visual inspection for wear, cracks, and proper tension' },
        { id: 5, title: 'Drive shafts and CV boots inspection', description: 'Check for damage, leaks, and proper operation' },
        { id: 6, title: 'Exhaust condition and leak check', description: 'Inspect entire exhaust system for leaks and damage' },
        { id: 7, title: 'Air filter inspection and cleaning', description: 'Clean or recommend replacement if necessary' },
        { id: 8, title: 'Lights and wipers check', description: 'Test all lights, indicators, and wiper operation' },
        { id: 9, title: 'Battery health test', description: 'Test battery voltage, charge capacity, and condition' },
        { id: 10, title: 'Tyre inspection', description: 'Check tread depth, wear pattern, and adjust pressures' },
        { id: 11, title: 'Customer concerns diagnostics', description: 'Scan and diagnose any specific issues you report' },
        { id: 12, title: 'Detailed report provided', description: 'Receive recommendations and repair plan before any additional work' },
      ]
    },
    'major-service': {
      name: 'Major Service',
      tagline: 'Comprehensive service for optimal vehicle performance',
      short_description: 'Comprehensive service for optimal vehicle performance',
      description: '<p>A thorough service including all minor service items plus additional checks and replacements. Recommended every 12 months or 15,000km.</p>',
      display_price: 'Contact for quote',
      display_duration: '2-3 hours',
      inclusions: [
        { id: 1, title: 'All Minor Service inclusions', description: 'Complete minor service package included' },
        { id: 2, title: 'Air filter replacement', description: 'Brand new air filter for optimal engine breathing' },
        { id: 3, title: 'Cabin filter replacement', description: 'Fresh cabin air filter for cleaner air conditioning' },
        { id: 4, title: 'Spark plug replacement', description: 'If required based on vehicle service schedule' },
        { id: 5, title: 'Coolant system check', description: 'Full coolant system inspection and top-up' },
        { id: 6, title: 'Transmission fluid check', description: 'Level and condition inspection' },
        { id: 7, title: 'Steering and suspension inspection', description: 'Comprehensive check of all components' },
        { id: 8, title: 'Exhaust system inspection', description: 'Full exhaust check for leaks and damage' },
        { id: 9, title: 'Detailed diagnostic report', description: 'Comprehensive vehicle health report' },
      ]
    },
    'brake-suspension': {
      name: 'Brake & Suspension',
      tagline: 'Expert brake and suspension repairs and maintenance',
      short_description: 'Expert brake and suspension repairs and maintenance',
      description: '<p>Keep your vehicle safe with our comprehensive brake and suspension services. We handle everything from brake pad replacement to full suspension overhauls.</p>',
      display_price: 'Contact for quote',
      display_duration: 'Varies by service',
      inclusions: [
        { id: 1, title: 'Brake pad and disc replacement', description: 'Quality brake components for safe stopping' },
        { id: 2, title: 'Brake fluid replacement', description: 'Fresh DOT fluid for optimal braking' },
        { id: 3, title: 'Shock absorber replacement', description: 'Restore ride comfort and handling' },
        { id: 4, title: 'Suspension components repair', description: 'Bushes, arms, and linkage repairs' },
        { id: 5, title: 'Brake system diagnostics', description: 'Full system check and diagnosis' },
        { id: 6, title: 'Road test and safety check', description: 'Verify repairs and safe operation' },
      ]
    },
    'brakes-clutch-suspension': {
      name: 'Brakes, Clutch & Suspension',
      tagline: 'Expert brake, clutch, and suspension repairs and maintenance',
      short_description: 'Expert brake, clutch, and suspension repairs and maintenance',
      description: '<p>Keep your vehicle safe and performing at its best with our comprehensive brake, clutch, and suspension services. Our expert technicians handle everything from routine maintenance to complete system overhauls.</p>',
      sections: [
        {
          id: 1,
          title: 'Brake System Services',
          content: '<ul><li>Testing and checking brake system for excessive shake, noise, squeaky turns etc.</li><li>Replacing new discs, drums, and brake pads</li><li>Brake shoe replacement</li><li>Wheel cylinder service and replacement</li><li>Brake sensors replacement</li></ul>'
        },
        {
          id: 2,
          title: 'Suspension Services',
          content: '<ul><li>Shock absorber replacement</li><li>Sway bar link replacement</li><li>Wheel bearing service and replacement</li><li>Control arms and bushes replacement</li></ul>'
        },
        {
          id: 3,
          title: 'Clutch Services',
          content: '<ul><li>Complete clutch replacement</li><li>Clutch adjustment and diagnostics</li></ul>'
        }
      ],
      additional_info: '<p>For quote, please contact us.</p>',
      display_price: 'Contact for Quote',
      inclusions: [
        { id: 1, title: 'Brake pad and disc replacement', description: 'Quality brake pads and discs for reliable stopping power' },
        { id: 2, title: 'Brake system testing and diagnostics', description: 'Check for shake, noise, squeaky turns' },
        { id: 3, title: 'Brake fluid replacement', description: 'Fresh brake fluid for optimal braking performance' },
        { id: 4, title: 'Brake discs and drums replacement', description: 'New rotors and drums as needed' },
        { id: 5, title: 'Brake system inspection', description: 'Check brake lines, hoses, calipers, and master cylinder' },
        { id: 6, title: 'Brake pads and shoes replacement', description: 'Quality brake pads and shoes' },
        { id: 7, title: 'Clutch inspection and replacement', description: 'Clutch disc, pressure plate, and release bearing service' },
        { id: 8, title: 'Wheel cylinder service', description: 'Repair or replace wheel cylinders' },
        { id: 9, title: 'Brake sensors replacement', description: 'Replace worn brake sensors' },
        { id: 10, title: 'Shock absorber replacement', description: 'New shocks for smooth ride' },
        { id: 11, title: 'Suspension components repair', description: 'Control arms, bushings, ball joints, and tie rod ends' },
        { id: 12, title: 'Sway bar link replacement', description: 'Stabilizer bar link service' },
        { id: 13, title: 'Wheel bearing service', description: 'Replace worn wheel bearings' },
        { id: 14, title: 'Road test and safety check', description: 'Comprehensive road test to verify proper operation' },
        { id: 15, title: 'Control arms and bushes', description: 'Replace control arms and worn bushes' },
        { id: 16, title: 'Clutch replacement', description: 'Complete clutch kit installation' },
      ]
    },
    'aircon-service': {
      name: 'Aircon Service',
      tagline: 'Professional air conditioning service and repairs',
      short_description: 'Professional air conditioning service and repairs',
      description: '<p>Licensed aircon servicing to keep you cool. We service, repair, and regas all vehicle air conditioning systems.</p>',
      display_price: 'Contact for quote',
      display_duration: '1 hour',
      license: 'AU NO: AU55615',
      inclusions: [
        { id: 1, title: 'Air conditioning system inspection', description: 'Full system check and diagnosis' },
        { id: 2, title: 'Refrigerant regas', description: 'Top up or full regas with quality refrigerant' },
        { id: 3, title: 'Leak detection', description: 'Find and identify any system leaks' },
        { id: 4, title: 'Compressor testing', description: 'Verify compressor operation and health' },
        { id: 5, title: 'Filter replacement', description: 'Clean cabin filter for fresh air' },
        { id: 6, title: 'Performance test', description: 'Verify cooling performance after service' },
      ]
    },
    'tyres-alignment': {
      name: 'Tyres & Wheel Alignment',
      tagline: 'Quality tyres and precision wheel alignment',
      short_description: 'Quality tyres and precision wheel alignment',
      description: '<p>We supply and fit all major tyre brands and provide precision wheel alignment services to ensure even tyre wear and optimal handling.</p>',
      display_price: 'Contact for quote',
      display_duration: 'Varies by service',
      inclusions: [
        { id: 1, title: 'Tyre supply and fitting', description: 'All major brands available' },
        { id: 2, title: '4-wheel alignment', description: 'Precision computerised alignment' },
        { id: 3, title: 'Wheel balancing', description: 'Eliminate vibrations for smooth driving' },
        { id: 4, title: 'Tyre rotation', description: 'Even out wear patterns' },
        { id: 5, title: 'Puncture repair', description: 'Quick and reliable repairs' },
        { id: 6, title: 'TPMS service', description: 'Tyre pressure monitoring system service' },
      ]
    },
    'battery-service': {
      name: 'Battery Service',
      tagline: 'Battery testing, service, and replacement',
      short_description: 'Battery testing, service, and replacement',
      description: '<p>Complete battery services including testing, charging, and replacement. We stock quality batteries for all makes and models.</p>',
      display_price: 'Contact for quote',
      display_duration: '30-60 minutes',
      inclusions: [
        { id: 1, title: 'Battery health testing', description: 'Comprehensive battery and charging system test' },
        { id: 2, title: 'Terminal cleaning and protection', description: 'Clean connections for reliable starting' },
        { id: 3, title: 'Battery replacement', description: 'Quality batteries with warranty' },
        { id: 4, title: 'Alternator testing', description: 'Verify charging system health' },
        { id: 5, title: 'Electrical system check', description: 'Check for parasitic drains' },
        { id: 6, title: 'Warranty coverage', description: 'All batteries come with manufacturer warranty' },
      ]
    }
  };

  const displayService = service || fallbackServices[slug] || fallbackServices[normalizedSlug] || null;

  // Use shared utility for consistent image across all pages
  const serviceImage = getServiceImage(displayService, slug);

  if (loading) {
    return (
      <div className="container section">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!displayService) {
    return (
      <div className="container section" style={{ textAlign: 'center' }}>
        <h2>Service Not Found</h2>
        <p>The service you're looking for doesn't exist.</p>
        <Link to="/services" className="btn btn-primary">
          View All Services
        </Link>
      </div>
    );
  }

  // Get display values
  const serviceTagline = displayService.tagline || displayService.short_description || '';
  const servicePricing = displayService.display_price || displayService.price_note || 'Contact for quote';
  const serviceDuration = displayService.display_duration || displayService.duration_text || null;
  const hasSections = displayService.sections && displayService.sections.length > 0;
  const hasAdditionalInfo = displayService.additional_info;
  const hasInclusions = displayService.inclusions && displayService.inclusions.length > 0;

  return (
    <>
      <Helmet>
        <title>{displayService.meta_title || `${displayService.name} - Automotive Care`}</title>
        <meta name="description" content={displayService.meta_description || displayService.short_description} />
        {displayService.meta_keywords && <meta name="keywords" content={displayService.meta_keywords} />}
      </Helmet>

      {/* Page Header */}
      <section className="page-header service-detail-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button onClick={() => navigate(-1)} className="back-button">
              <FaArrowLeft /> Back
            </button>
            <h1>{displayService.name}</h1>
            {serviceTagline && <p className="service-tagline">{serviceTagline}</p>}
          </motion.div>
        </div>
      </section>

      {/* Service Details */}
      <section className="section service-detail-section">
        <div className="container">
          <div className="service-detail-grid">
            {/* Main Content */}
            <motion.div
              className="service-main-content"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Service Overview */}
              <div className="service-description">
                <h2>Service Overview</h2>
                <div
                  className="service-overview-content"
                  dangerouslySetInnerHTML={{
                    __html: displayService.description || displayService.short_description
                  }}
                />
              </div>

              {/* Dynamic Content Sections */}
              {hasSections && displayService.sections.map((section, index) => (
                <div key={section.id || index} className="service-section content-section">
                  <h3><FaClipboardList /> {section.title}</h3>
                  <div
                    className="section-content"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                </div>
              ))}

              {/* Additional Info */}
              {hasAdditionalInfo && (
                <div className="service-section additional-info-section">
                  <div
                    className="section-content highlight-box"
                    dangerouslySetInnerHTML={{ __html: displayService.additional_info }}
                  />
                </div>
              )}

              {/* What's Included */}
              {hasInclusions && (
                <div className="service-features">
                  <h3><FaWrench /> What's Included</h3>
                  <div className="inclusions-grid">
                    {displayService.inclusions.map((inclusion, index) => (
                      <motion.div
                        key={inclusion.id || index}
                        className="inclusion-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.03 }}
                      >
                        <div className="inclusion-header">
                          <FaCheckCircle className="check-icon" />
                          <h4>{inclusion.title}</h4>
                        </div>
                        {inclusion.description && (
                          <p className="inclusion-description">{inclusion.description}</p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              className="service-sidebar"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="service-info-card">
                <h3>Service Information</h3>

                <div className="info-item">
                  <h4>Pricing</h4>
                  <p className="price-highlight">{servicePricing}</p>
                </div>

                {serviceDuration && (
                  <div className="info-item">
                    <h4><FaClock /> Duration</h4>
                    <p>{serviceDuration}</p>
                  </div>
                )}

                {displayService.license && (
                  <div className="info-item license-badge">
                    <p className="small-text">{displayService.license}</p>
                  </div>
                )}

                <button type="button" className="btn btn-primary btn-block btn-lg" onClick={openBookingModal}>
                  Book This Service
                </button>

                <div className="contact-info-box">
                  <h4>Questions?</h4>
                  <p>Call us for more information</p>
                  <a href={`tel:${companyPhone}`} className="phone-link">
                    <FaPhoneAlt /> {formattedPhone}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Image */}
      {serviceImage && (
        <section className="service-detail-image-section">
          <div className="service-detail-image-wrapper">
            <div className="service-detail-image-glow"></div>
            <div className="service-detail-image-gradient"></div>
            <div className="floating-detail-circle floating-detail-circle-1"></div>
            <div className="floating-detail-circle floating-detail-circle-2"></div>
            <img
              src={serviceImage}
              alt={`${displayService.name} illustration`}
              className="service-detail-image"
              loading="lazy"
            />
            <div className="service-detail-image-hover-overlay"></div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section bg-gray">
        <div className="container">
          <div className="cta-box">
            <h3>Need a different service?</h3>
            <p>Check out our full range of automotive services</p>
            <Link to="/services" className="btn btn-outline btn-lg">
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
